import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { extractYouTubeId } from "../lib/youtube";
import { Leaf, ArrowRight, PlayCircle, ShieldCheck, HeartPulse, Sparkles } from "lucide-react";
import { GoogleAuthButton } from "../components/GoogleAuthButton";

export default function Welcome() {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/site-settings").then(({ data }) => setSettings(data)).catch(() => setSettings({}));
  }, []);

  const ytId = extractYouTubeId(settings?.welcome_video_youtube_id);
  const title = settings?.welcome_title || "Welcome to Danashakaa.com";
  const subtitle = settings?.welcome_subtitle || "Your calmer, healthier rhythm starts here.";
  const ctaLabel = settings?.welcome_cta_label || "Create your free profile";

  return (
    <div className="min-h-screen bg-bone relative overflow-hidden" data-testid="welcome-page">
      {/* Decorative blobs */}
      <div className="absolute -top-40 -right-40 w-[640px] h-[640px] rounded-full bg-moss/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-terra/10 blur-3xl pointer-events-none" />

      {/* Minimal header */}
      <header className="relative z-10">
        <div className="container-page flex items-center justify-between h-20">
          <Link to="/welcome" className="flex items-center gap-2" data-testid="welcome-logo">
            <span className="w-10 h-10 rounded-full bg-moss text-white grid place-items-center">
              <Leaf size={20} />
            </span>
            <span className="font-heading text-2xl font-semibold tracking-tight">Danashakaa.com</span>
          </Link>
          <Link to="/login" className="text-sm font-medium text-muted2 hover:text-moss" data-testid="welcome-login-link">
            I already have an account
          </Link>
        </div>
      </header>

      {/* Main */}
      <main className="relative z-10 container-page pt-6 pb-20">
        <div className="max-w-3xl mx-auto text-center animate-fade-in-up">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-moss/10 text-moss text-xs font-semibold px-3 py-1">
            <Sparkles size={12} /> A warm welcome
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight leading-[1.05] text-ink">
            {title}
          </h1>
          <p className="mt-5 text-base md:text-lg text-muted2 leading-relaxed">
            {subtitle}
          </p>
        </div>

        {/* Video card */}
        <div className="mt-12 max-w-4xl mx-auto animate-fade-in-up" style={{ animationDelay: "120ms" }}>
          <div className="relative rounded-3xl overflow-hidden border border-subtle bg-white shadow-hover">
            <div className="relative aspect-video bg-cardm">
              {ytId ? (
                <iframe
                  title="Welcome video"
                  src={`https://www.youtube.com/embed/${ytId}?rel=0&modestbranding=1`}
                  className="absolute inset-0 w-full h-full"
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  data-testid="welcome-video-iframe"
                />
              ) : (
                <div className="absolute inset-0 grid place-items-center text-center p-8" data-testid="welcome-video-placeholder">
                  <div>
                    <span className="inline-flex w-16 h-16 rounded-full bg-moss text-white items-center justify-center mb-4">
                      <PlayCircle size={32} />
                    </span>
                    <p className="font-heading font-semibold text-ink">Welcome video coming soon</p>
                    <p className="text-sm text-muted2 mt-1">An admin can add a YouTube video from the dashboard.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 max-w-md mx-auto text-center animate-fade-in-up" style={{ animationDelay: "240ms" }}>
          <Link
            to="/register"
            data-testid="welcome-cta-register"
            className="w-full inline-flex items-center justify-center gap-2 bg-terra hover:bg-terra-dark text-white rounded-full px-8 py-4 font-semibold text-base transition-all active:scale-95 shadow-ambient"
          >
            {ctaLabel} <ArrowRight size={18} />
          </Link>
          <div className="my-5 flex items-center gap-3 text-xs text-soft">
            <span className="flex-1 h-px bg-subtle" />
            or
            <span className="flex-1 h-px bg-subtle" />
          </div>
          <GoogleAuthButton label="Continue with Google" testId="welcome-google-button" />
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-muted2">
            <span className="inline-flex items-center gap-2"><ShieldCheck size={16} className="text-leaf" /> Free profile</span>
            <span className="inline-flex items-center gap-2"><HeartPulse size={16} className="text-leaf" /> Personalised guidance</span>
            <span className="inline-flex items-center gap-2"><PlayCircle size={16} className="text-leaf" /> Expert videos</span>
          </div>
        </div>
      </main>

      <footer className="relative z-10 border-t border-subtle bg-white/40">
        <div className="container-page py-6 text-center text-xs text-soft">
          © {new Date().getFullYear()} Danashakaa.com. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
